import React, { useState } from 'react';
import { Alert, AlertDescription } from '../ui/alert';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { AlertTriangle, CreditCard, RotateCcw } from 'lucide-react';
import { useTheme } from '../ui/theme-provider';
import { cn } from '../../lib/utils';

const ExpiredSubscriptionDisplay = ({
  subscription,
  loading,
  refreshing,
  onRefresh,
  onManageSubscription,
  onSelectPlan,
  stripeCustomerId
}) => {
  const { theme } = useTheme();
  const [showProDetails, setShowProDetails] = useState(false);
  const [manageLoading, setManageLoading] = useState(false);
  const [proUpgradeLoading, setProUpgradeLoading] = useState(false);

  // Format the expiration date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    try {
      const date = new Date(timestamp * 1000);
      return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch (error) {
      return 'Unknown';
    }
  };

  const expirationDate = subscription?.currentPeriodEnd || subscription?.trialEnd;
  const planLevel = subscription?.level || 'PRO';
  const planName = planLevel.charAt(0).toUpperCase() + planLevel.slice(1).toLowerCase();

  const handleManageSubscription = async () => {
    setManageLoading(true);
    try {
      if (onManageSubscription) {
        await onManageSubscription();
      }
    } catch (error) {
      console.error('Error managing subscription:', error);
    } finally {
      // Keep loading state until redirect happens
      // Don't reset loading here as we're redirecting
    }
  };

  const handleProUpgrade = async () => {
    setProUpgradeLoading(true);
    try {
      // Use the current subscription level to determine the correct plan
      const currentLevel = subscription?.level || 'basic';
      if (onSelectPlan) {
        await onSelectPlan(currentLevel, false);
      }
    } catch (error) {
      console.error('Error upgrading to pro:', error);
      setProUpgradeLoading(false);
    }
    // Don't reset loading here as we're redirecting to Stripe
  };

  const handleProUpgradeFromExpandable = async () => {
    setProUpgradeLoading(true);
    try {
      if (onSelectPlan) {
        await onSelectPlan('pro', false);
      }
    } catch (error) {
      console.error('Error upgrading to pro:', error);
      setProUpgradeLoading(false);
    }
    // Don't reset loading here as we're redirecting to Stripe
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Expired Subscription Alert */}
      <Alert variant="destructive" className="border-2 border-red-400 bg-red-50/80 dark:bg-red-950/50 backdrop-blur-sm shadow-lg">
        <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5 text-red-500 flex-shrink-0" />
        <div className="ml-2 sm:ml-3 min-w-0">
          <h4 className="text-base sm:text-lg font-semibold text-red-800 dark:text-red-200 mb-1 sm:mb-2 leading-tight">
            Your {planName} Subscription Has Expired
          </h4>
          <AlertDescription className="text-red-700 dark:text-red-300 text-sm sm:text-base leading-relaxed">
            Your {planName} plan subscription expired on{' '}
            <span className="font-semibold whitespace-nowrap">{formatDate(expirationDate)}</span>.
            {subscription?.status === 'past_due' ? 
              ' Please update your payment method to restore access to premium features.' :
              ' Please renew your subscription to continue using premium features.'
            }
          </AlertDescription>
        </div>
      </Alert>

      {/* Subscription Details Card */}
      <Card className={cn(
        "border-2 shadow-lg",
        theme === 'light' 
          ? 'bg-white border-gray-200' 
          : 'bg-[#2A2A3E] border-gray-700'
      )}>
        <CardHeader className="pb-3 sm:pb-4">
          <CardTitle className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
            <span className="text-base sm:text-lg font-semibold">Subscription Details</span>
            <Badge variant="destructive" className="text-xs sm:text-sm w-fit">
              Expired
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <p className="text-xs sm:text-sm font-medium text-muted-foreground">Plan</p>
              <p className="text-base sm:text-lg font-semibold leading-tight">{planName} Plan</p>
            </div>
            <div>
              <p className="text-xs sm:text-sm font-medium text-muted-foreground">Status</p>
              <p className="text-base sm:text-lg font-semibold text-red-600 dark:text-red-400 leading-tight">
                {subscription?.status === 'past_due' ? 'Payment Required' : 'Expired'}
              </p>
            </div>
            <div className="sm:col-span-2">
              <p className="text-xs sm:text-sm font-medium text-muted-foreground">Expired On</p>
              <p className="text-base sm:text-lg font-semibold leading-tight">{formatDate(expirationDate)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3">
        {subscription?.status === 'past_due' ? (
          <Button 
            onClick={handleManageSubscription}
            disabled={loading || refreshing || !stripeCustomerId || manageLoading || proUpgradeLoading}
            className={cn(
              "w-full sm:w-auto flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white border-0 shadow-lg hover:shadow-xl transition-all duration-200",
              "min-h-[56px] py-4 px-6 text-sm sm:text-base font-medium" // Consistent text sizing
            )}
            size="lg"
          >
            {manageLoading ? (
              <div className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="text-sm sm:text-base whitespace-nowrap">Opening Payment Portal...</span>
              </div>
            ) : (
              <div className="flex items-center justify-center">
                <CreditCard className="w-4 h-4 mr-2 flex-shrink-0" />
                <span className="text-sm sm:text-base font-medium whitespace-nowrap">Update Payment Method</span>
              </div>
            )}
          </Button>
        ) : (
          <Button 
            onClick={handleProUpgrade}
            disabled={loading || refreshing || proUpgradeLoading || manageLoading}
            className={cn(
              "w-full sm:w-auto flex-1 bg-gradient-to-r from-pink-500 to-orange-500 hover:from-pink-600 hover:to-orange-600 text-white border-0 shadow-lg hover:shadow-xl transition-all duration-200",
              "min-h-[56px] py-4 px-6 text-sm sm:text-base font-medium"
            )}
            size="lg"
          >
            {proUpgradeLoading ? (
              <div className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="text-sm sm:text-base whitespace-nowrap">Opening Payment Portal...</span>
              </div>
            ) : (
              <div className="flex items-center justify-center">
                <CreditCard className="w-4 h-4 mr-2 flex-shrink-0" />
                <span className="text-sm sm:text-base font-medium">Renew {planName} Subscription</span>
              </div>
            )}
          </Button>
        )}
        
        <Button 
          onClick={onRefresh}
          disabled={loading || refreshing || manageLoading || proUpgradeLoading}
          variant="outline" 
          size="lg"
          className={cn(
            "w-full sm:w-auto min-h-[56px] py-4 px-6 text-sm sm:text-base font-medium", // Consistent sizing
            theme === 'light' 
              ? 'text-gray-700 border-gray-300 hover:bg-gray-50 hover:text-gray-900' 
              : 'text-gray-300 border-gray-600 hover:bg-gray-700 hover:text-white'
          )}
        >
          <RotateCcw className={cn("w-4 h-4 mr-2 flex-shrink-0", refreshing && "animate-spin")} />
          <span className="text-sm sm:text-base whitespace-nowrap">{refreshing ? 'Refreshing...' : 'Refresh Status'}</span>
        </Button>
      </div>

      {/* Stripe Secure Payment Note */}
      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
        {/* Lock icon for security */}
        <svg className="w-3 h-3 sm:w-4 sm:h-4 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <rect x="3" y="11" width="18" height="10" rx="2" stroke="currentColor" strokeWidth="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="2" />
        </svg>
        <span className="leading-tight text-xs sm:text-xs">
          Secure payment by Stripe. We never store your card details.
        </span>
      </div>

      {/* PRO Upgrade Section - More Prominent Design */}
      <Card className={cn(
        "border-2 shadow-md",
        theme === 'light' 
          ? 'bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-200' 
          : 'bg-gradient-to-br from-purple-900/20 to-indigo-900/20 border-purple-600'
      )}>
        <CardContent className="p-4 sm:p-6">
          {/* Mobile-first responsive header */}
          <div className="space-y-3 sm:space-y-0 sm:flex sm:items-center sm:justify-between mb-4">
            <div className="flex-1">
              <h4 className="text-lg sm:text-xl font-semibold text-purple-800 dark:text-purple-200 leading-tight">
                Consider Upgrading to PRO
              </h4>
              <p className="text-sm text-purple-600 dark:text-purple-300 mt-1">
                Get more features and priority support
              </p>
            </div>
            <div className="flex-shrink-0">
              <Button
                className="w-full sm:w-auto bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white border-0 shadow-md hover:shadow-lg transition-all duration-200 text-sm px-4 py-2"
                onClick={() => setShowProDetails((v) => !v)}
                variant="default"
                size="sm"
                disabled={manageLoading || proUpgradeLoading}
              >
                {showProDetails ? "Hide Details" : "See Benefits"}
              </Button>
            </div>
          </div>
          
          {showProDetails && (
            <div className="space-y-4">
              <div className={cn(
                "p-3 sm:p-4 rounded-lg border",
                theme === 'light' 
                  ? 'bg-white/70 border-purple-200' 
                  : 'bg-black/20 border-purple-700'
              )}>
                <h5 className="font-medium text-purple-800 dark:text-purple-200 mb-3 text-sm sm:text-base">
                  PRO Plan Features:
                </h5>
                <ul className="space-y-2 text-sm text-purple-700 dark:text-purple-300">
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-purple-500 flex-shrink-0"></div>
                    <span>Advanced analytics & reporting</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-purple-500 flex-shrink-0"></div>
                    <span>Expenses tracking & management</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-purple-500 flex-shrink-0"></div>
                    <span>Complete invoice history</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-purple-500 flex-shrink-0"></div>
                    <span>Priority support</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-purple-500 flex-shrink-0"></div>
                    <span>Unlimited invoices & clients</span>
                  </li>
                </ul>
              </div>
              <Button
                className={cn(
                  "w-full bg-gradient-to-r from-pink-500 to-orange-500 hover:from-pink-600 hover:to-orange-600 text-white border-0 shadow-lg hover:shadow-xl transition-all duration-200",
                  "min-h-[52px] py-4 px-6 text-base font-medium"
                )}
                onClick={handleProUpgradeFromExpandable}
                disabled={loading || proUpgradeLoading || manageLoading}
                size="lg"
              >
                {proUpgradeLoading ? (
                  <div className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="text-sm sm:text-base">Opening Payment Portal...</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    <CreditCard className="w-4 h-4 mr-2 flex-shrink-0" />
                    <span className="text-sm sm:text-base font-medium">Upgrade to PRO Plan</span>
                  </div>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Help Text */}
      <div className={cn(
        "p-4 rounded-lg border",
        theme === 'light' 
          ? 'bg-blue-50 border-blue-200' 
          : 'bg-blue-950/30 border-blue-700'
      )}>
        <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">
          Need Help?
        </h4>
        <p className="text-blue-700 dark:text-blue-300 text-sm">
          If you're experiencing issues with your subscription or payments, 
          please contact our support team. We're here to help restore your access quickly.
        </p>
      </div>
    </div>
  );
};

export default ExpiredSubscriptionDisplay;
